import type { CornerRadiusModifierValue } from "schemas/generated/fileModifiers";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import AllCorners from "@svg/modifiers/direction/all-corners";
import BottomLeftCorner from "@svg/modifiers/direction/bottom-left-corner";
import BottomRightCorner from "@svg/modifiers/direction/bottom-right-corner";
import TopLeftCorner from "@svg/modifiers/direction/top-left-corner";
import TopRightCorner from "@svg/modifiers/direction/top-right-corner";

import { useSemanticModifierValue } from "@agent-ui/features/modifiers/hooks/useSemanticModifierValue";
import { LengthInputSelector } from "@v2/features/modifiers/components/LengthInputSelector";
import { ChevronDown } from "lucide-react";
import { MAX_CORNER_RADIUS } from "replo-runtime/shared/constants";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { parseUnit } from "replo-runtime/shared/utils/units";

type RadiusMode = "unified" | "individual";
type CornerRadiusValue = number | "full";

const clampRadius = (value: number): number => {
  return Math.min(Math.max(value, 0), MAX_CORNER_RADIUS);
};

const normalizeRadiusValue = (value: number): CornerRadiusValue => {
  if (value >= MAX_CORNER_RADIUS) {
    return "full";
  }
  return value;
};

const CORNER_RADIUS_OPTIONS = [
  { value: 0, label: "None" },
  { value: 4, label: "Small" },
  { value: 8, label: "Medium" },
  { value: 12, label: "Large" },
  { value: 16, label: "Extra Large" },
  { value: 24, label: "2X Large" },
  { value: 40, label: "3X Large" },
  { value: 72, label: "4X Large" },
  { value: "full", label: "Full" },
] as const;

interface RadiusValue {
  topLeft?: CornerRadiusValue;
  topRight?: CornerRadiusValue;
  bottomRight?: CornerRadiusValue;
  bottomLeft?: CornerRadiusValue;
}

interface CornerRadiusModifierProps {
  onChange: (modifiers: CornerRadiusModifierValue[]) => void;
  onModeChange?: (isIndividual: boolean) => void;
  showButton?: boolean;
  mode?: "unified" | "individual";
  showIndividualInputs?: boolean;
  onlyShowIndividualInputs?: boolean;
}

// NOTE (Derek, 2026-02-19): Parse CSS border-radius shorthand into 4 corner values.
// The computed value can be "10px" (uniform), "10px 20px" (vertical/horizontal),
// "10px 20px 30px" (top-left, top-right/bottom-left, bottom-right),
// or "10px 20px 30px 40px" (clockwise from top-left).
function parseBorderRadiusCorners(borderRadius: string | undefined): {
  topLeft: number;
  topRight: number;
  bottomRight: number;
  bottomLeft: number;
} {
  if (!borderRadius) {
    return { topLeft: 0, topRight: 0, bottomRight: 0, bottomLeft: 0 };
  }

  const parts = borderRadius.split(" ").map((p) => {
    const match = p.match(/^(\d+(?:\.\d+)?)/);
    return match?.[1] ? Number.parseFloat(match[1]) : 0;
  });

  if (parts.length === 1) {
    const v = parts[0]!;
    return { topLeft: v, topRight: v, bottomRight: v, bottomLeft: v };
  }
  if (parts.length === 2) {
    return {
      topLeft: parts[0]!,
      topRight: parts[1]!,
      bottomRight: parts[0]!,
      bottomLeft: parts[1]!,
    };
  }
  if (parts.length === 3) {
    return {
      topLeft: parts[0]!,
      topRight: parts[1]!,
      bottomRight: parts[2]!,
      bottomLeft: parts[1]!,
    };
  }
  return {
    topLeft: parts[0]!,
    topRight: parts[1]!,
    bottomRight: parts[2]!,
    bottomLeft: parts[3]!,
  };
}

const CornerRadiusModifier: React.FC<CornerRadiusModifierProps> = ({
  onChange,
  onModeChange,
  mode: externalMode,
  showIndividualInputs = false,
  onlyShowIndividualInputs = false,
}) => {
  const parsedCorners = useSemanticModifierValue({
    semanticExtractor: ({ computedStyles }) => {
      return parseBorderRadiusCorners(computedStyles.borderRadius);
    },
  });

  const extractedRadius = useMemo(() => {
    const corners = parsedCorners ?? {
      topLeft: 0,
      topRight: 0,
      bottomRight: 0,
      bottomLeft: 0,
    };
    return {
      topLeft: normalizeRadiusValue(corners.topLeft),
      topRight: normalizeRadiusValue(corners.topRight),
      bottomRight: normalizeRadiusValue(corners.bottomRight),
      bottomLeft: normalizeRadiusValue(corners.bottomLeft),
    };
  }, [parsedCorners]);

  const [radiusValue, setRadiusValue] =
    useOverridableState<RadiusValue>(extractedRadius);

  const [internalRadiusMode] = useState<RadiusMode>(() => {
    const { topLeft, topRight, bottomRight, bottomLeft } = extractedRadius;
    if (
      topLeft === topRight &&
      topLeft === bottomRight &&
      topLeft === bottomLeft
    ) {
      return "unified";
    }
    return "individual";
  });

  const radiusMode = externalMode ?? internalRadiusMode;

  useEffect(() => {
    onModeChange?.(radiusMode === "individual");
  }, [radiusMode, onModeChange]);

  const handleRadiusChange = useCallback(
    (radius: Partial<RadiusValue>) => {
      const newRadius = { ...radiusValue, ...radius };
      if (radiusMode === "unified") {
        const unifiedValue = newRadius.topLeft;
        newRadius.topRight = unifiedValue;
        newRadius.bottomRight = unifiedValue;
        newRadius.bottomLeft = unifiedValue;
      }
      setRadiusValue(newRadius);

      const modifiers: CornerRadiusModifierValue[] = [];

      const getCornerValue = (value?: CornerRadiusValue): number | "full" => {
        if (value === "full") {
          return "full";
        }
        return value ?? 0;
      };

      modifiers.push(
        {
          type: "cornerRadius",
          corner: "topLeft",
          value: getCornerValue(newRadius.topLeft),
        },
        {
          type: "cornerRadius",
          corner: "topRight",
          value: getCornerValue(newRadius.topRight),
        },
        {
          type: "cornerRadius",
          corner: "bottomRight",
          value: getCornerValue(newRadius.bottomRight),
        },
        {
          type: "cornerRadius",
          corner: "bottomLeft",
          value: getCornerValue(newRadius.bottomLeft),
        },
      );

      onChange(modifiers);
    },
    [radiusValue, radiusMode, onChange, setRadiusValue],
  );

  const getIndividualCornerRadiusDisplayValue = (
    value?: CornerRadiusValue,
  ): string => {
    if (value === "full") {
      return "Full";
    }
    if (!value) {
      return "0";
    }
    return clampRadius(value).toString();
  };

  const getUnifiedDisplayValue = (): string => {
    const { topLeft, topRight, bottomRight, bottomLeft } = radiusValue;
    const allCornersEqual =
      topLeft === topRight && topLeft === bottomRight && topLeft === bottomLeft;
    if (!allCornersEqual) {
      return "Mixed";
    }
    return getIndividualCornerRadiusDisplayValue(topLeft);
  };

  const parseRadiusValue = (newValue: string): CornerRadiusValue => {
    if (newValue === "full") {
      return "full";
    }
    const result = parseUnit(
      newValue,
      { value: 0, unit: "px" },
      "default",
      "px",
    );
    return Number(result.value) ?? 0;
  };

  const mappedRadiusOptions = CORNER_RADIUS_OPTIONS.map((option) => ({
    ...option,
    value: option.value.toString(),
  }));

  if (onlyShowIndividualInputs) {
    return (
      <div className="grid grid-cols-2 gap-2 items-start">
        <LengthInputSelector
          field="topLeftRadius"
          value={getIndividualCornerRadiusDisplayValue(radiusValue.topLeft)}
          onChange={(newValue) => {
            return handleRadiusChange({ topLeft: parseRadiusValue(newValue) });
          }}
          placeholder="0px"
          metrics={["px", "%"]}
          unitDefaults={{ value: "0", unit: "px" }}
          menuOptions={mappedRadiusOptions}
          minValues={{ px: 0, "%": 0 }}
          maxValues={{ px: MAX_CORNER_RADIUS, "%": 100 }}
          inputClassName="w-full"
          startEnhancer={<TopLeftCorner className="w-3 h-3" />}
          endEnhancer={() => <ChevronDown size={12} />}
        />
        <LengthInputSelector
          field="topRightRadius"
          value={getIndividualCornerRadiusDisplayValue(radiusValue.topRight)}
          onChange={(newValue) => {
            return handleRadiusChange({ topRight: parseRadiusValue(newValue) });
          }}
          placeholder="0px"
          metrics={["px", "%"]}
          unitDefaults={{ value: "0", unit: "px" }}
          menuOptions={mappedRadiusOptions}
          minValues={{ px: 0, "%": 0 }}
          maxValues={{ px: MAX_CORNER_RADIUS, "%": 100 }}
          inputClassName="w-full"
          startEnhancer={<TopRightCorner className="w-3 h-3" />}
          endEnhancer={() => <ChevronDown size={12} />}
        />
        <LengthInputSelector
          field="bottomLeftRadius"
          value={getIndividualCornerRadiusDisplayValue(radiusValue.bottomLeft)}
          onChange={(newValue) => {
            return handleRadiusChange({
              bottomLeft: parseRadiusValue(newValue),
            });
          }}
          placeholder="0px"
          metrics={["px", "%"]}
          unitDefaults={{ value: "0", unit: "px" }}
          menuOptions={mappedRadiusOptions}
          minValues={{ px: 0, "%": 0 }}
          maxValues={{ px: MAX_CORNER_RADIUS, "%": 100 }}
          inputClassName="w-full"
          startEnhancer={<BottomLeftCorner className="w-3 h-3" />}
          endEnhancer={() => <ChevronDown size={12} />}
        />
        <LengthInputSelector
          field="bottomRightRadius"
          value={getIndividualCornerRadiusDisplayValue(radiusValue.bottomRight)}
          onChange={(newValue) => {
            return handleRadiusChange({
              bottomRight: parseRadiusValue(newValue),
            });
          }}
          placeholder="0px"
          metrics={["px", "%"]}
          unitDefaults={{ value: "0", unit: "px" }}
          menuOptions={mappedRadiusOptions}
          minValues={{ px: 0, "%": 0 }}
          maxValues={{ px: MAX_CORNER_RADIUS, "%": 100 }}
          inputClassName="w-full"
          startEnhancer={<BottomRightCorner className="w-3 h-3" />}
          endEnhancer={() => <ChevronDown size={12} />}
        />
      </div>
    );
  }

  const handleUnifiedRadiusChange = (newValue: string) => {
    const parsedValue = parseRadiusValue(newValue);
    if (showIndividualInputs) {
      onModeChange?.(false);
      handleRadiusChange({
        topLeft: parsedValue,
        topRight: parsedValue,
        bottomRight: parsedValue,
        bottomLeft: parsedValue,
      });
    } else {
      handleRadiusChange({ topLeft: parsedValue });
    }
  };

  return (
    <div className="corner-radius-modifier h-6">
      <LengthInputSelector
        field="unifiedRadius"
        value={getUnifiedDisplayValue()}
        onChange={handleUnifiedRadiusChange}
        placeholder="0px"
        metrics={["px", "%"]}
        unitDefaults={{ value: "0", unit: "px" }}
        menuOptions={mappedRadiusOptions}
        minValues={{ px: 0, "%": 0 }}
        maxValues={{ px: MAX_CORNER_RADIUS, "%": 100 }}
        allowedNonUnitValues={["Mixed", "full"]}
        inputClassName="w-full"
        startEnhancer={<AllCorners className="w-3 h-3" />}
        endEnhancer={() => <ChevronDown size={12} />}
      />
    </div>
  );
};

export default CornerRadiusModifier;
